import { Logger } from "../utils/logger.js";
const _CrossDomainStorage = class _CrossDomainStorage {
  constructor(bridgeOrigin, stage, bridgePath = "/bridge.html") {
    this.bridge = null;
    this.cache = /* @__PURE__ */ new Map();
    this.isReady = false;
    this.isSyncing = null;
    this.maxKeyLength = 1024;
    this.maxValueLength = 1024 * 1024;
    this.maxStorageSize = 10 * 1024 * 1024;
    this.usingFallback = false;
    this.logger = new Logger("CrossDomainStorage", stage);
    if (typeof stage !== "string" || !stage.trim()) {
      throw new Error("Invalid stage - must be a non-empty string");
    }
    this.bridgeOrigin = bridgeOrigin;
    this.bridgePath = bridgePath;
    this.stage = stage.trim();
    this.fallbackPrefix = `cds_${this.stage}_`;
    this.senderId = "storage_" + Math.random().toString(36).substr(2, 9) + "_" + Date.now();
    this.getItem = this.getItem.bind(this);
    this.setItem = this.setItem.bind(this);
    this.removeItem = this.removeItem.bind(this);
    this.clear = this.clear.bind(this);
  }
  static createInstance(bridgeOrigin, stage, bridgePath = "/bridge.html") {
    const key = `${bridgeOrigin}_${stage}_${bridgePath}`;
    if (!_CrossDomainStorage.instanceByOrigin.has(key)) {
      const instance2 = new _CrossDomainStorage(bridgeOrigin, stage, bridgePath);
      _CrossDomainStorage.instanceByOrigin.set(key, instance2);
    }
    const instance = _CrossDomainStorage.instanceByOrigin.get(key);
    if (!instance) {
      throw new Error(`CrossDomainStorage instance not found for key: ${key}`);
    }
    _CrossDomainStorage.currentInstance = instance;
    return _CrossDomainStorage.currentInstance;
  }
  static getCurrentInstance() {
    if (!_CrossDomainStorage.currentInstance) {
      return null;
    }
    return _CrossDomainStorage.currentInstance;
  }
  /**
   * Async sync function - call this once to pull all data from bridge domain
   */
  async initialize() {
    if (this.isSyncing) {
      return this.isSyncing;
    }
    this.isSyncing = this.performSync();
    try {
      await this.isSyncing;
    } finally {
      this.isSyncing = null;
    }
  }
  async performSync() {
    try {
      if (!this.bridge) {
        this.logger.debug(`Initializing bridge for stage '${this.stage}' with origin '${this.bridgeOrigin}'`);
        await this.initializeBridge();
      }
      const allData = await this.sendMessage({
        action: "getAll",
        messageId: this.generateMessageId(),
        stage: this.stage
      });
      this.logger.debug(`Received all data from bridge:`, allData);
      this.cache.clear();
      if (allData && typeof allData === "object") {
        let totalSize = 0;
        for (const [key, value] of Object.entries(allData)) {
          if (typeof key === "string" && typeof value === "string") {
            totalSize += key.length + value.length;
            if (totalSize > this.maxStorageSize) {
              this.logger.warn("Storage size limit exceeded, truncating data");
              break;
            }
            this.cache.set(key, value);
          }
        }
      }
      this.isReady = true;
    } catch (error) {
      this.logger.warn(`Bridge sync failed for stage '${this.stage}', falling back to localStorage:`, error);
      await this.initializeFallback();
    }
  }
  /**
   * Initialize fallback mode using localStorage
   */
  async initializeFallback() {
    try {
      this.usingFallback = true;
      this.cache.clear();
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key == null ? void 0 : key.startsWith(this.fallbackPrefix)) {
          const storageKey = key.substring(this.fallbackPrefix.length);
          const value = localStorage.getItem(key);
          if (value !== null) {
            this.cache.set(storageKey, value);
          }
        }
      }
      this.isReady = true;
      this.logger.info(`Fallback mode initialized for stage '${this.stage}'. Loaded ${this.cache.size} items from localStorage`);
    } catch (error) {
      this.logger.error(`Failed to initialize localStorage fallback:`, error);
      throw new Error("Both bridge and localStorage fallback failed");
    }
  }
  // Storage API Implementation - All synchronous
  getItem(key) {
    this.ensureReady();
    this.validateKey(key);
    const value = this.cache.get(key) ?? null;
    return value;
  }
  setItem(key, value) {
    this.ensureReady();
    this.validateKey(key);
    this.validateValue(value);
    const currentSize = this.getStorageInfo().totalSize;
    const newItemSize = key.length + value.length;
    const oldValue = this.cache.get(key);
    const oldItemSize = oldValue ? key.length + oldValue.length : 0;
    if (currentSize - oldItemSize + newItemSize > this.maxStorageSize) {
      this.logger.error(`Storage quota exceeded. Current: ${currentSize}, New item: ${newItemSize}, Max: ${this.maxStorageSize}`);
      throw new Error("Storage quota exceeded");
    }
    this.cache.set(key, value);
    this.updateBridge("setItem", key, value);
  }
  removeItem(key) {
    this.ensureReady();
    this.validateKey(key);
    this.cache.delete(key);
    this.updateBridge("removeItem", key);
  }
  clear() {
    this.ensureReady();
    const itemCount = this.cache.size;
    this.cache.clear();
    this.logger.debug(`clear() - Removed ${itemCount} items ${this.usingFallback ? "(fallback mode)" : ""}`);
    this.updateBridge("clear");
  }
  key(index) {
    this.ensureReady();
    if (index < 0 || index >= this.cache.size) {
      return null;
    }
    return Array.from(this.cache.keys())[index] ?? null;
  }
  get length() {
    this.ensureReady();
    return this.cache.size;
  }
  // Additional utility methods
  /**
   * Get the current stage
   */
  get currentStage() {
    return this.stage;
  }
  /**
   * Get all keys as array
   */
  keys() {
    this.ensureReady();
    return Array.from(this.cache.keys());
  }
  /**
   * Check if storage is ready (synced)
   */
  get ready() {
    return this.isReady;
  }
  /**
   * Check if using localStorage fallback mode
   */
  get usingLocalStorageFallback() {
    return this.usingFallback;
  }
  /**
   * Get the storage mode description
   */
  get storageMode() {
    return this.usingFallback ? "localStorage fallback" : "cross-domain bridge";
  }
  // Private methods
  ensureReady() {
    if (!this.isReady) {
      throw new Error("CrossDomainStorage not ready. Call sync() first.");
    }
  }
  async initializeBridge() {
    return new Promise((resolve, reject) => {
      var _a;
      const bridgeId = this.hashString(`cross-domain-storage-${this.stage}`);
      const existingBridge = document.getElementById(bridgeId);
      if (existingBridge) {
        this.logger.debug(`Reusing existing bridge iframe with ID: ${bridgeId}`);
        this.bridge = existingBridge;
        resolve();
        return;
      }
      if ((_a = this.bridge) == null ? void 0 : _a.parentNode) {
        this.logger.debug("Removing old bridge iframe before creating new one");
        this.bridge.parentNode.removeChild(this.bridge);
      }
      const url = `${this.bridgeOrigin}${this.bridgePath}`;
      this.bridge = document.createElement("iframe");
      this.bridge.id = bridgeId;
      this.bridge.src = url;
      this.bridge.style.display = "none";
      this.bridge.style.width = "0";
      this.bridge.style.height = "0";
      this.bridge.setAttribute("data-stage", this.stage);
      this.bridge.setAttribute("data-added-by", this.senderId);
      this.bridge.setAttribute("data-timestamp", (/* @__PURE__ */ new Date()).toISOString());
      this.bridge.onload = () => {
        var _a2;
        try {
          const contentWindow = (_a2 = this.bridge) == null ? void 0 : _a2.contentWindow;
          if (contentWindow) {
            resolve();
          } else {
            this.logger.error(`Failed to load storage bridge iframe with ID: ${bridgeId} and origin: ${this.bridgeOrigin} and stage: ${this.stage}`);
            reject(new Error("Failed to load storage bridge"));
          }
        } catch (error) {
          this.logger.error(`Failed to load storage bridge iframe with ID: ${bridgeId} and origin: ${this.bridgeOrigin} and stage: ${this.stage}`, error);
          reject(new Error("Failed to load storage bridge iframe"));
        }
      };
      this.bridge.onerror = () => {
        this.logger.error("Failed to load storage bridge iframe");
        reject(new Error("Failed to load storage bridge"));
      };
      document.body.appendChild(this.bridge);
      this.logger.debug(`Created new bridge iframe with ID: ${bridgeId}`);
    });
  }
  /**
   * Simple hash function to create short, consistent identifiers
   */
  hashString(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString(36);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }
  async sendMessage(message) {
    var _a;
    if (!((_a = this.bridge) == null ? void 0 : _a.contentWindow)) {
      this.logger.error("Bridge not initialized when trying to send message");
      throw new Error("Bridge not initialized");
    }
    this.logger.debug(`Sending message: ${message.action} (${message.messageId})`);
    return new Promise((resolve, reject) => {
      var _a2, _b;
      const messageId = message.messageId;
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          window.removeEventListener("message", handler);
          this.logger.error(`Storage bridge timeout for action: ${message.action} (${messageId})`);
          reject(new Error(`Storage bridge timeout for action: ${message.action}`));
        }
      }, 2e3);
      const handler = (event) => {
        if (event.origin !== this.bridgeOrigin) {
          this.logger.warn(`Ignoring message from invalid origin: ${event.origin} (expected: ${this.bridgeOrigin})`);
          return;
        }
        if (!event.data || typeof event.data !== "object") {
          this.logger.debug("Ignoring message with invalid structure:", event.data);
          return;
        }
        if (event.data.messageId !== messageId) {
          this.logger.debug(`Ignoring message with wrong ID: ${event.data.messageId} (expected: ${messageId})`);
          return;
        }
        if (event.data.senderId === this.senderId) {
          this.logger.debug(`Ignoring message from self: ${event.data.senderId}`);
          return;
        }
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          window.removeEventListener("message", handler);
          if (event.data.success === false) {
            this.logger.error(`Storage operation failed: ${event.data.error || "Unknown error"}`);
            reject(new Error(event.data.error || "Storage operation failed"));
          } else {
            resolve(event.data.data);
          }
        }
      };
      window.addEventListener("message", handler);
      try {
        const messageWithSender = { ...message, senderId: this.senderId };
        (_b = (_a2 = this.bridge) == null ? void 0 : _a2.contentWindow) == null ? void 0 : _b.postMessage(messageWithSender, this.bridgeOrigin);
        this.logger.debug(`Message posted to bridge: ${message.action} (${messageId})`);
      } catch (error) {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          window.removeEventListener("message", handler);
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          this.logger.error(`Failed to send message to bridge: ${errorMessage}`);
          reject(new Error(`Failed to send message: ${errorMessage}`));
        }
      }
    });
  }
  updateBridge(action, key, value) {
    var _a;
    if (this.usingFallback) {
      try {
        switch (action) {
          case "setItem":
            if (key && value !== void 0) {
              localStorage.setItem(this.fallbackPrefix + key, value);
            }
            break;
          case "removeItem":
            if (key) {
              localStorage.removeItem(this.fallbackPrefix + key);
            }
            break;
          case "clear": {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const storageKey = localStorage.key(i);
              if (storageKey == null ? void 0 : storageKey.startsWith(this.fallbackPrefix)) {
                keysToRemove.push(storageKey);
              }
            }
            keysToRemove.forEach((k) => localStorage.removeItem(k));
            break;
          }
        }
        this.logger.debug(`Background fallback update: ${action} ${key ? `(${key})` : ""}`);
      } catch (error) {
        this.logger.warn(`Failed to update localStorage in fallback mode:`, error);
      }
      return;
    }
    if (!((_a = this.bridge) == null ? void 0 : _a.contentWindow)) {
      this.logger.warn("Bridge not available for background update");
      return;
    }
    try {
      const messageId = this.generateMessageId();
      this.bridge.contentWindow.postMessage({
        action,
        key,
        value,
        messageId,
        senderId: this.senderId,
        stage: this.stage
      }, this.bridgeOrigin);
      this.logger.debug(`Background bridge update: ${action} (${messageId})`);
    } catch (error) {
      this.logger.warn("Failed to update bridge storage:", error);
    }
  }
  generateMessageId() {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
  // Cleanup
  destroy() {
    var _a;
    this.logger.info(`Destroying CrossDomainStorage instance (mode: ${this.storageMode})`);
    if ((_a = this.bridge) == null ? void 0 : _a.parentNode) {
      const bridgeId = this.bridge.id;
      const stage = this.bridge.getAttribute("data-stage");
      this.logger.debug(`Detaching from bridge iframe with ID: ${bridgeId} (stage: ${stage})`);
      this.bridge = null;
    }
    const itemCount = this.cache.size;
    this.cache.clear();
    this.isReady = false;
    this.usingFallback = false;
    this.logger.debug(`Cleared cache (${itemCount} items) and reset ready state`);
  }
  /**
   * Force remove the bridge iframe (use with caution)
   * This will affect all storage instances using the same bridge
   */
  forceDestroyBridge() {
    var _a;
    if ((_a = this.bridge) == null ? void 0 : _a.parentNode) {
      const bridgeId = this.bridge.id;
      this.logger.warn(`Force removing bridge iframe with ID: ${bridgeId}`);
      this.bridge.parentNode.removeChild(this.bridge);
      this.bridge = null;
    }
  }
  /**
   * Set the log level for this storage instance
   * @param level - The log level to set (DEBUG, INFO, WARN, ERROR, NONE)
   */
  setLogLevel(level) {
    this.logger.setLogLevel(level);
    this.logger.info(`Log level changed to ${level}`);
  }
  /**
   * Get current storage usage information
   */
  getStorageInfo() {
    const totalSize = this.keys().reduce((acc, key) => {
      var _a;
      return acc + key.length + (((_a = this.getItem(key)) == null ? void 0 : _a.length) ?? 0);
    }, 0);
    const utilizationPercent = totalSize / this.maxStorageSize * 100;
    const info = {
      itemCount: this.cache.size,
      totalSize,
      maxSize: this.maxStorageSize,
      utilizationPercent: Math.round(utilizationPercent * 100) / 100,
      ready: this.isReady,
      stage: this.stage,
      usingFallback: this.usingFallback,
      storageMode: this.storageMode
    };
    this.logger.debug("Storage info requested:", info);
    return info;
  }
  // Private validation methods
  validateKey(key) {
    if (typeof key !== "string") {
      throw new TypeError("Storage key must be a string");
    }
    if (key.length === 0) {
      throw new Error("Storage key cannot be empty");
    }
    if (key.length > this.maxKeyLength) {
      throw new Error(`Storage key too long (max ${this.maxKeyLength} characters)`);
    }
    if (key === "__proto__" || key === "constructor" || key === "prototype") {
      throw new Error("Invalid storage key");
    }
  }
  validateValue(value) {
    if (typeof value !== "string") {
      throw new TypeError("Storage value must be a string");
    }
    if (value.length > this.maxValueLength) {
      throw new Error(`Storage value too long (max ${this.maxValueLength} characters)`);
    }
  }
};
_CrossDomainStorage.instanceByOrigin = /* @__PURE__ */ new Map();
_CrossDomainStorage.currentInstance = null;
let CrossDomainStorage = _CrossDomainStorage;
export {
  CrossDomainStorage as default
};
//# sourceMappingURL=CrossDomainStorage.js.map
